import { useState } from "react"
import {
  Download, FileText, CalendarDays, BookOpen, FileCheck, ClipboardList,
  FileType2, AlertCircle, Eye, Clock, FileQuestion,
} from "lucide-react"
import { SiteShell } from "../../components/site/SiteShell"
import { PageHero } from "../../components/site/PageHero"
import { Card, CardContent } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Badge } from "../../components/ui/Badge"
import { formatDate } from "../../lib/constants"
import { usePublicDownloads } from "../../hooks/usePublic"

const categories = [
  { key: "ALL", label: "All", icon: FileText },
  { key: "TIMETABLE", label: "Timetables", icon: CalendarDays },
  { key: "MATERIAL", label: "Course Materials", icon: BookOpen },
  { key: "HANDBOOK", label: "Handbooks", icon: FileCheck },
  { key: "FORM", label: "Forms", icon: ClipboardList },
  { key: "EXAM", label: "Examination", icon: FileCheck },
  { key: "GENERAL", label: "General", icon: FileType2 },
]

const categoryTone: Record<string, string> = {
  TIMETABLE: "success",
  MATERIAL: "warning",
  HANDBOOK: "info",
  FORM: "default",
  EXAM: "danger",
  GENERAL: "outline",
}

export default function Downloads() {
  const [filter, setFilter] = useState("ALL")
  const { data, isLoading } = usePublicDownloads()
  const all = data?.downloads || []

  const filtered = filter === "ALL" ? all : all.filter(d => d.category === filter)

  return (
    <SiteShell>
      <PageHero
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Downloads" }]}
        subtitle="Resources"
        title="Downloads & Resource Centre"
        description="Access timetables, course materials, handbooks, official forms, examination past questions and other academic resources. All files are scanned for security and provided free of charge to students and the public."
      />

      {/* Filter chips */}
      <section className="py-10">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-2">
            {categories.map(c => {
              const count = c.key === "ALL" ? all.length : all.filter(d => d.category === c.key).length
              const active = filter === c.key
              return (
                <button
                  key={c.key}
                  onClick={() => setFilter(c.key)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
                    active
                      ? "bg-brand-800 dark:bg-brand-700 text-white border-brand-800 dark:border-brand-700"
                      : "bg-white dark:bg-ink-900 text-ink-700 dark:text-ink-300 border-ink-200 dark:border-ink-800 hover:border-brand-400 dark:hover:border-brand-600"
                  }`}
                >
                  <c.icon className="w-4 h-4" />
                  {c.label}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? "bg-white/20" : "bg-ink-100 dark:bg-ink-800"}`}>{count}</span>
                </button>
              )
            })}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <Card key={i} className="border-0 shadow-md">
                  <CardContent className="pt-6">
                    <div className="animate-pulse space-y-3">
                      <div className="w-12 h-12 bg-ink-200 dark:bg-ink-800 rounded-lg" />
                      <div className="h-4 bg-ink-200 dark:bg-ink-800 rounded w-3/4" />
                      <div className="h-3 bg-ink-200 dark:bg-ink-800 rounded w-1/2" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <Card className="border-0 shadow-md">
              <CardContent className="py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-ink-100 dark:bg-ink-800 grid place-items-center mx-auto mb-4">
                  <FileQuestion className="w-8 h-8 text-ink-400" />
                </div>
                <h3 className="font-display font-bold text-lg text-ink-900 dark:text-ink-100 mb-2">No downloads available</h3>
                <p className="text-sm text-ink-500 dark:text-ink-400 max-w-md mx-auto">
                  There are no resources in this category at the moment. Please check back later or browse another category.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map(d => (
                <Card key={d.id} className="card-lift border-0 shadow-md">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-lg bg-brand-50 dark:bg-brand-900/40 grid place-items-center">
                        <FileText className="w-6 h-6 text-brand-700 dark:text-brand-400" />
                      </div>
                      <Badge variant={(categoryTone[d.category] as any) || "default"}>{d.category}</Badge>
                    </div>
                    <h3 className="font-display font-bold text-base text-ink-900 dark:text-ink-100 leading-tight mb-2">{d.title}</h3>
                    {d.description && (
                      <p className="text-sm text-ink-600 dark:text-ink-400 leading-relaxed line-clamp-2 mb-4">{d.description}</p>
                    )}
                    <div className="flex items-center gap-3 text-xs text-ink-500 dark:text-ink-400 mb-4">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {formatDate(d.created_at)}
                      </span>
                      {d.download_count != null && (
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" /> {d.download_count} downloads
                        </span>
                      )}
                    </div>
                    {d.file_url ? (
                      <a
                        href={d.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-full"
                      >
                        <Button size="sm" className="w-full bg-brand-800 dark:bg-brand-700">
                          <Download className="w-4 h-4 mr-1.5" /> Download
                        </Button>
                      </a>
                    ) : (
                      <Button size="sm" variant="outline" className="w-full" disabled>
                        <AlertCircle className="w-4 h-4 mr-1.5" /> Unavailable
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Info banner */}
      <section className="py-12 bg-white dark:bg-ink-900">
        <div className="container mx-auto px-4">
          <Card className="border-0 shadow-md bg-gradient-to-br from-brand-50 to-gold-50 dark:from-brand-900/20 dark:to-gold-900/20">
            <CardContent className="pt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-brand-700 grid place-items-center shrink-0">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-lg text-ink-900 dark:text-ink-100 mb-1">Student Resources</h3>
                  <p className="text-sm text-ink-600 dark:text-ink-400 leading-relaxed">
                    Additional resources — including course materials, examination past questions and personalized timetables — are available to enrolled students through the Student Portal.
                  </p>
                </div>
              </div>
              <a href="/portal/login" className="shrink-0">
                <Button className="bg-brand-800 dark:bg-brand-700">
                  Student Portal
                </Button>
              </a>
            </CardContent>
          </Card>
        </div>
      </section>
    </SiteShell>
  )
}
